import React from "react";
import moment from "moment";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import Enderecos from "../../common/query/Enderecos";
import Emails from "../../common/query/Emails";
import Telefones from "../../common/query/Telefones";
import Sociedade from "../../common/query/Sociedade";
import {
  countDadosCadastrais,
  formatDocument,
  formatToReal,
} from "../../../utils/utils";
import { FaRegChartBar, FaUserCheck } from "react-icons/fa";
import { IoReceiptOutline } from "react-icons/io5";
import { LuBuilding2, LuMapPin } from "react-icons/lu";
import {
  GrDocumentExcel,
  GrDocumentText,
  GrDocumentTime,
} from "react-icons/gr";
import { CiMoneyCheck1 } from "react-icons/ci";
import { MdOutlineBalance, MdOutlineEmail } from "react-icons/md";
import {
  HiOutlineCurrencyDollar,
  HiOutlineLocationMarker,
} from "react-icons/hi";
import { FiPhone, FiUserCheck, FiUsers } from "react-icons/fi";

const buildDurationLabel = (fromMoment, suffix = "") => {
  if (!fromMoment || !fromMoment.isValid()) return null;
  const years = moment().diff(fromMoment, "years");
  const afterYears = fromMoment.clone().add(years, "years");
  const months = moment().diff(afterYears, "months");
  const parts = [];
  if (years) parts.push(`${years} ano${years === 1 ? "" : "s"}`);
  if (months) parts.push(`${months} mês${months === 1 ? "" : "es"}`);
  if (!parts.length) return `menos de 1 mês${suffix ? ` ${suffix}` : ""}`;
  const joined = parts.join(" e ");
  return suffix ? `${joined} ${suffix}` : joined;
};

const hasOwnKey = (obj, key) =>
  Object.prototype.hasOwnProperty.call(obj || {}, key);

const BR_DATE_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;

const getDisplayDate = (value) => {
  if (!value) return null;
  const dateStr = String(value).trim();
  const brMatch = dateStr.match(/^(\d{2}\/\d{2}\/\d{4})/);
  if (brMatch) return brMatch[1];
  if (BR_DATE_REGEX.test(dateStr)) return dateStr;
  const parsed = moment(dateStr, [moment.ISO_8601, "YYYY-MM-DD"], true);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : null;
};

const getMomentDate = (value) => {
  if (!value) return null;
  const displayDate = getDisplayDate(value);
  if (!displayDate) return null;
  const parsed = moment(displayDate, "DD/MM/YYYY", true);
  return parsed.isValid() ? parsed : null;
};

const CadastralPJQuery = ({ data, dataFull }) => {
  const cadastro = data?.dadosCadastrais || {};
  const foundationSource =
    (hasOwnKey(cadastro, "dataFundacao") && cadastro.dataFundacao) ||
    (hasOwnKey(cadastro, "dataInicioAtividade") &&
      cadastro.dataInicioAtividade) ||
    null;
  const foundationMoment = getMomentDate(foundationSource);
  const foundationLabel = getDisplayDate(foundationSource);
  const foundationAgeLabel = buildDurationLabel(
    foundationMoment,
    "de atividade",
  );
  const situacaoLabel =
    (hasOwnKey(cadastro, "situacaoCadastral")
      ? cadastro.situacaoCadastral
      : cadastro.situacao) || "";

  const hasCnaesSecundariosKey = hasOwnKey(cadastro, "cnaEsSecundarios");
  const cnaesSecundarios = Array.isArray(cadastro.cnaEsSecundarios)
    ? cadastro.cnaEsSecundarios
    : [];

  const hasFiliaisKey =
    hasOwnKey(cadastro, "filiais") || hasOwnKey(data, "filiais");
  const filiais = Array.isArray(cadastro.filiais)
    ? cadastro.filiais
    : Array.isArray(data?.filiais)
      ? data.filiais
      : [];

  const summaryCards = [
    hasOwnKey(cadastro, "cnpj") && {
      label: "CNPJ / MF",
      value: formatDocument(cadastro.cnpj) || "Não disponível",
      hint: "Documento",
    },
    (hasOwnKey(cadastro, "cnaeDescricao") ||
      hasOwnKey(cadastro, "ramoAtividade") ||
      hasOwnKey(cadastro, "cnaePrincipal")) && {
      label: "CNAE principal",
      value:
        cadastro.cnaeDescricao ||
        cadastro.ramoAtividade ||
        cadastro.cnaePrincipal ||
        "Não informado",
      hint: "Atividade",
    },
    (hasOwnKey(cadastro, "naturezaJuridica") ||
      hasOwnKey(cadastro, "naturezaJuridicaDescricao") ||
      hasOwnKey(cadastro, "natureza")) && {
      label: "Natureza jurídica",
      value:
        cadastro.naturezaJuridica ||
        cadastro.naturezaJuridicaDescricao ||
        cadastro.natureza ||
        "Não informado",
      hint: "Perfil",
    },
  ].filter(Boolean);

  const additionalCards = [
    hasOwnKey(cadastro, "situacaoEspecial") && {
      label: "Situação especial",
      value: cadastro.situacaoEspecial || "Não informado",
      hint: "Cadastro",
    },
    hasOwnKey(cadastro, "naturezaJuridicaTipo") && {
      label: "Tipo natureza",
      value: cadastro.naturezaJuridicaTipo || "Não informado",
      hint: "Natureza",
    },
    hasOwnKey(cadastro, "porte") && {
      label: "Porte",
      value: cadastro.porte || "Não informado",
      hint: "Empresa",
    },
    hasOwnKey(cadastro, "faixaFuncionarios") && {
      label: "Faixa funcionários",
      value: cadastro.faixaFuncionarios || "Não informado",
      hint: "Equipe",
    },
    hasOwnKey(cadastro, "quantidadeFuncionarios") && {
      label: "Qtd. funcionários",
      value:
        cadastro.quantidadeFuncionarios === 0
          ? 0
          : cadastro.quantidadeFuncionarios || "Não informado",
      hint: "Equipe",
    },
    hasOwnKey(cadastro, "tributacao") && {
      label: "Tributação",
      value: cadastro.tributacao || "Não informado",
      hint: "Fiscal",
    },
    hasOwnKey(cadastro, "opcaoMEI") && {
      label: "Opção MEI",
      value: cadastro.opcaoMEI || "Não informado",
      hint: "Regime",
    },
    hasOwnKey(cadastro, "opcaoSimples") && {
      label: "Opção Simples",
      value: cadastro.opcaoSimples || "Não informado",
      hint: "Regime",
    },
    hasOwnKey(cadastro, "orgaoPublico") && {
      label: "Órgão público",
      value: cadastro.orgaoPublico || "Não informado",
      hint: "Setor",
    },
    hasOwnKey(cadastro, "ramo") && {
      label: "Ramo",
      value: cadastro.ramo || "Não informado",
      hint: "Atividade",
    },
    hasOwnKey(cadastro, "tipoEmpresa") && {
      label: "Tipo empresa",
      value: cadastro.tipoEmpresa || "Não informado",
      hint: "Classificação",
    },
    hasOwnKey(cadastro, "quantidadeFiliais") && {
      label: "Qtd. filiais",
      value: cadastro.quantidadeFiliais || "Não informado",
      hint: "Estrutura",
    },
    hasOwnKey(cadastro, "ultimaAtualizacaoPJ") && {
      label: "Última atualização",
      value: getDisplayDate(cadastro.ultimaAtualizacaoPJ) || "Não informado",
      hint: "Base",
    },
  ].filter(Boolean);

  const hasFinanceiroGroup =
    hasOwnKey(cadastro, "faixaFaturamento") ||
    hasOwnKey(cadastro, "faturamentoMedioCNAE") ||
    hasOwnKey(cadastro, "faturamentoPresumido");

  return (
    <>
      <>
        <TitleStripe>
          <div className="flex items-center gap-2">
            <LuBuilding2 className="text-blue-700" />{" "}
            <span>Dados Cadastrais</span>
          </div>
        </TitleStripe>
        <div className="text-xs">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                Razão Social
              </p>
              <p className="text-xl font-semibold text-gray-900">
                {cadastro.razaoSocial || "Indisponível"}
              </p>
              <p className="text-[11px] text-gray-500">
                {cadastro.nomeFantasia || "Sem nome fantasia"}
              </p>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
              Situação: {situacaoLabel || "Desconhecida"}
            </span>
            {foundationSource && (
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                Fundação: {foundationLabel || "—"}
              </span>
            )}
            {foundationAgeLabel && (
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
                {foundationAgeLabel}
              </span>
            )}
          </div>

          {summaryCards.length > 0 && (
            <div className="mt-3 grid gap-2 grid-cols-3">
              {summaryCards.map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    {item.label}
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {item.value}
                  </p>
                  <p className="text-[10px] text-gray-500">{item.hint}</p>
                </div>
              ))}
            </div>
          )}

          {additionalCards.length > 0 && (
            <div className="mt-3 grid gap-2 grid-cols-4">
              {additionalCards.map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2"
                >
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    {item.label}
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {item.value}
                  </p>
                  <p className="text-[10px] text-gray-500">{item.hint}</p>
                </div>
              ))}
            </div>
          )}

          {hasFinanceiroGroup && (
            <div className="mt-3 rounded-2xl border border-emerald-100 bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800">
                  Perfil Financeiro
                </p>
                <span className="rounded-full border border-emerald-200 bg-white/80 px-2 py-0.5 text-[10px] text-emerald-700">
                  Informações estimadas
                </span>
              </div>
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                As informações do perfil financeiro são aproximadas e podem
                variar conforme a fonte consultada.
              </p>
              <div className="mt-3 grid gap-2 grid-cols-3">
                {hasOwnKey(cadastro, "faixaFaturamento") && (
                  <div className="rounded-xl border border-emerald-200/70 bg-white/90 px-3 py-2 shadow-sm">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-600">
                      Faixa faturamento
                    </p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {cadastro.faixaFaturamento || "Não informado"}
                    </p>
                  </div>
                )}
                {hasOwnKey(cadastro, "faturamentoMedioCNAE") && (
                  <div className="rounded-xl border border-teal-200/70 bg-white/90 px-3 py-2 shadow-sm">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-teal-600">
                      Médio por CNAE
                    </p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {cadastro.faturamentoMedioCNAE || "Não informado"}
                    </p>
                  </div>
                )}
                {hasOwnKey(cadastro, "faturamentoPresumido") && (
                  <div className="rounded-xl border border-cyan-200/70 bg-white/90 px-3 py-2 shadow-sm">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-700">
                      Faturamento presumido
                    </p>
                    <p className="mt-1 text-sm font-semibold text-gray-900">
                      {cadastro.faturamentoPresumido
                        ? formatToReal(cadastro.faturamentoPresumido)
                        : "Não informado"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {hasCnaesSecundariosKey && (
            <div className="mt-3">
              <TitleStripe>
                <div className="flex items-center gap-2">
                  <LuBuilding2 className="text-blue-700" />
                  <span>CNAEs Secundários</span>
                </div>
              </TitleStripe>
              {cnaesSecundarios.length > 0 ? (
                <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white">
                  <table className="min-w-full text-left text-xs">
                    <thead className="bg-slate-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 font-medium">Código</th>
                        <th className="px-3 py-2 font-medium">Descrição</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cnaesSecundarios.map((cnae, index) => (
                        <tr
                          key={`${cnae?.cnaeCodigoSecundario || "cnae"}-${index}`}
                          className={
                            index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                          }
                        >
                          <td className="px-3 py-3">
                            {cnae?.cnaeCodigoSecundario === 0
                              ? 0
                              : cnae?.cnaeCodigoSecundario || "-"}
                          </td>
                          <td className="px-3 py-3">
                            {cnae?.cnaeDescricaoSecundario || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <NoOcurrence />
              )}
            </div>
          )}

          {hasFiliaisKey && (
            <div className="mt-3">
              <TitleStripe>
                <div className="flex items-center gap-2">
                  <FiUsers className="text-blue-700" />
                  <span>Filiais</span>
                </div>
              </TitleStripe>
              {filiais.length > 0 ? (
                <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200 bg-white">
                  <table className="min-w-full text-left text-xs">
                    <thead className="bg-slate-50 text-gray-600">
                      <tr>
                        <th className="px-3 py-2 font-medium">CNPJ</th>
                        <th className="px-3 py-2 font-medium">Razão social</th>
                        <th className="px-3 py-2 font-medium">UF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filiais.map((filial, index) => (
                        <tr
                          key={`${filial?.cnpj || "filial"}-${index}`}
                          className={
                            index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                          }
                        >
                          <td className="px-3 py-2">
                            {formatDocument(filial?.cnpj) ||
                              filial?.cnpj ||
                              "-"}
                          </td>
                          <td className="px-3 py-2">
                            {filial?.razaoSocial || "-"}
                          </td>
                          <td className="px-3 py-2">{filial?.uf || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <NoOcurrence />
              )}
            </div>
          )}
        </div>
      </>

      {data?.socios && (
        <div>
          <TitleStripe className="mb-5">
            <div className="flex items-center gap-2">
              <FiUsers className="text-blue-700" />
              <span>Quadro Societário</span>
            </div>
          </TitleStripe>
          {data?.socios?.length > 0 ? (
            <Sociedade socios={data?.socios} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.enderecos && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <HiOutlineLocationMarker className="text-blue-700" />{" "}
              <span>Histórico de Endereços</span>
            </div>
          </TitleStripe>
          {data?.enderecos?.length > 0 ? (
            <Enderecos enderecos={data?.enderecos} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.emails && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <MdOutlineEmail className="text-blue-700" />{" "}
              <span>Histórico de E-mails</span>
            </div>
          </TitleStripe>
          {data?.emails?.length > 0 ? (
            <Emails emails={data?.emails} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.telefones && (
        <div>
          <TitleStripe>
            <div className="flex items-center gap-2">
              <FiPhone className="text-blue-700" />{" "}
              <span>Histórico de Telefones</span>
            </div>
          </TitleStripe>
          {data?.telefones?.length > 0 ? (
            <Telefones telefones={data?.telefones} />
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}
    </>
  );
};

export default CadastralPJQuery;
